import { PropsWithChildren, ReactElement, ReactNode } from 'react';
import parse from 'html-react-parser';
import * as prod from 'react/jsx-runtime';
import { unified } from 'unified';
import rehypeParse from 'rehype-parse';
import rehypeReact, { Options } from 'rehype-react';
import { rehypeFilter, RehypeFilterOptions } from './rehypeFilter';

type ParseHtmlOptions = Pick<Options, 'components'> & RehypeFilterOptions;

const production = { Fragment: prod.Fragment, jsx: prod.jsx, jsxs: prod.jsxs };

export function parseHtml(
  content: string,
  options?: ParseHtmlOptions,
): ReactNode {
  const { result } = unified()
    .use(rehypeParse, {
      fragment: true,
    })
    // @ts-expect-error: rehypeFilter plugin overloads are incompatible with current unified typing.
    .use(rehypeFilter, {
      allowElement: options?.allowElement,
      allowedElements: options?.allowedElements,
      disallowedElements: options?.disallowedElements,
      unwrapDisallowed: options?.unwrapDisallowed,
    })
    .use(rehypeReact, production)
    .processSync(content);

  // unwraps the outer <div> element
  return (result as ReactElement<PropsWithChildren<{}>>).props.children;
}

export function cleanUpRawHtml(content: string) {
  const parseOptions = {
    replace(domNode) {
      if (domNode.name === 'a') {
        if (
          domNode.attribs &&
          domNode.attribs.href &&
          (() => {
            const url = domNode.attribs.href;
            const allowedHosts = ['content.liftwithboystown.org'];
            const urlPattern = /^(?:https?:\/\/)?(?:www\.)?([^\/]+)/i;
            const match = url.match(urlPattern);
            const host = match ? match[1] : '';
            return allowedHosts.includes(host);
          })() &&
          domNode.attribs.href.endsWith('.pdf')
        ) {
          // check to see if there is a rel="nofollow" attribute
          let addedNoFollow = false;
          for (const key in domNode.attribs) {
            if (key === 'rel') {
              if (!domNode.attribs[key].includes('nofollow')) {
                domNode.attribs[key] += ' nofollow';
                addedNoFollow = true;
              } else if (domNode.attribs[key].includes('nofollow')) {
                addedNoFollow = true;
              }
            }
          }
          if (!addedNoFollow) {
            domNode.attribs['rel'] = 'nofollow';
          }
          // put the a back together including any added rel=nofollow
          let a = '<a ';
          for (const key in domNode.attribs) {
            a += `${key}="${domNode.attribs[key]}" `;
          }
          a += `>${domNode.children[0].data}</a>`;
          return parse(a);
        }
      }
    },
  };

  const parsed = parse(content, parseOptions);
  return parsed;
}
